/**
 * LocationChooser (DNKADM-0033 U1) — the app-bar service-location selector that
 * **replaces the tenant chip** in `AppLayout`'s `AppBar`. It shows the selected
 * location's `name` (with the `apartment` glyph) as the trigger; opening it lists
 * the tenant's locations and choosing one calls `setSelectedId`.
 *
 * It reads the shared {@link useServiceLocation} context (loaded fail-closed via
 * the existing `getServiceLocations`) and renders its three states:
 *  - **loading** → a disabled skeleton chip (never an empty/blank trigger);
 *  - **error** → a small inline error affordance with **Retry** (re-invokes the
 *    load) — never a silent empty;
 *  - **ready** → the trigger + a `Menu` of locations (the selected one checked).
 *
 * Tenant identity stays implicit (resolved server-side from the JWT) — there is no
 * "Tenant ·" label here, per the spec.
 */
import { useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { COURT_GREEN, Icon, resolveMaps } from '@/theme';
import { useServiceLocation } from '@/location/useServiceLocation';
import type { ConnectionStatus, ServiceLocation } from '@/api/operatingHours';
import { scrubBrand } from '@/utils/whitelabel';

/**
 * Payment-connection health dot (DNKADM-0043). Maps `connectionStatus.overall`
 * to a themed color + a default label (used only when the backend `label` is
 * absent — the contract's `label` is preferred and shown as-is). Replaces the
 * whole-tenant Settings payments chip removed by DNKADM-0042; the fix path is the
 * Connections screen.
 *
 * Color map (overall → theme token):
 *  - `healthy`        → `success.main`  (green)
 *  - `degraded`       → `warning.main`  (amber)
 *  - `down`           → `error.main`    (red)
 *  - `not_activated`  → `info.main`     (neutral/info — "configured but off")
 *  - `unconfigured`   → `text.disabled` (muted grey)
 *  - unknown/null     → `text.disabled` (muted grey — present but unrecognized)
 */
const DOT_COLOR: Record<NonNullable<ConnectionStatus['overall']>, string> = {
  healthy: 'success.main',
  degraded: 'warning.main',
  down: 'error.main',
  not_activated: 'info.main',
  unconfigured: 'text.disabled',
};

/** A fallback label per state, used only when the backend `label` is absent. */
const DOT_FALLBACK_LABEL: Record<NonNullable<ConnectionStatus['overall']>, string> = {
  healthy: 'Connected',
  degraded: 'Degraded',
  down: 'Connection down',
  not_activated: 'Not activated',
  unconfigured: 'Not configured',
};

/** The muted color for an `unconfigured` / unknown `overall`. */
const MUTED_DOT_COLOR = 'text.disabled';

/**
 * The accessible label for the dot: the backend `label`, else a per-state
 * fallback. The backend `label` is human copy rendered as-is (visible text AND
 * the dot's `aria-label`/`title`), so it is brand-scrubbed (DNKADM-0044) once
 * here at the render boundary — both consumers get the scrubbed value. The
 * fallbacks are clean local strings, so the scrub is a no-op on them.
 */
function dotLabel(status: ConnectionStatus): string {
  if (status.label) return scrubBrand(status.label)!;
  if (status.overall) return DOT_FALLBACK_LABEL[status.overall];
  return 'Payment connection status unknown';
}

/**
 * A short non-secret detail line for the tooltip, under the `label`: the live ping
 * error (when present), else the stored validation detail / status. Only the
 * contract's non-secret fields (`live.error`, `validation.detail`,
 * `validation.status`) are ever shown; backend-authored detail text is scrubbed
 * at this render boundary.
 */
function dotDetail(status: ConnectionStatus): string | null {
  if (status.live?.error) return scrubBrand(status.live.error);
  if (status.validation?.detail) return scrubBrand(status.validation.detail);
  if (status.validation?.status === 'valid') return 'Last tested valid';
  if (status.validation?.status === 'invalid') return 'Last test invalid — re-test in Connections';
  return null;
}

/**
 * The health dot for one location's `connectionStatus`. Renders nothing when the
 * status object is absent (forward-compatible — the field rolls out per-location).
 * The dot carries an `aria-label` with the backend `label` (not color-only) via the
 * `Icon` `title`, and a keyboard-reachable `Tooltip` leading with that `label`.
 */
function ConnectionDot({ status }: { status: ConnectionStatus | null }) {
  if (status === null) return null;

  const label = dotLabel(status);
  const detail = dotDetail(status);
  const color = status.overall ? DOT_COLOR[status.overall] : MUTED_DOT_COLOR;

  return (
    <Tooltip
      title={
        <>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {label}
          </Box>
          {detail ? (
            <Box component="span" sx={{ display: 'block', opacity: 0.85 }}>
              {detail}
            </Box>
          ) : null}
        </>
      }
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <Icon name="fiber_manual_record" title={label} sx={{ fontSize: 12, color }} />
      </Box>
    </Tooltip>
  );
}

export function LocationChooser() {
  const { locations, selectedId, selected, setSelectedId, status, retry } =
    useServiceLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Mode-aware tints (DNKADM-0293) — the light-only GREEN_TINTS const painted a
  // light selected row in dark mode.
  const maps = resolveMaps(useTheme());

  if (status === 'loading') {
    return (
      <Skeleton
        variant="rounded"
        width={180}
        height={34}
        role="status"
        aria-label="Loading service locations"
        sx={{ borderRadius: 2 }}
      />
    );
  }

  if (status === 'error') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Icon name="error" sx={{ fontSize: 20, color: 'error.main' }} />
        <Typography noWrap sx={{ fontSize: 13, color: 'error.main' }}>
          Couldn’t load locations
        </Typography>
        <Button size="small" color="inherit" onClick={retry} sx={{ minWidth: 0 }}>
          Retry
        </Button>
      </Box>
    );
  }

  // status === 'ready'
  const triggerLabel = selected?.name ?? selected?.id ?? '—';
  // The selected location's full record (for its connectionStatus on the trigger);
  // `selected` is narrowed to { id, name }, so resolve from the loaded list.
  const selectedLocation: ServiceLocation | undefined = locations.find(
    (l) => l.id === selectedId,
  );
  const open = Boolean(anchor);

  const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const handleClose = () => setAnchor(null);
  const handleSelect = (id: string) => {
    setSelectedId(id);
    setAnchor(null);
  };

  return (
    <>
      <Tooltip title="Service location">
        <Button
          color="inherit"
          onClick={handleOpen}
          aria-haspopup="menu"
          aria-expanded={open ? true : undefined}
          aria-label={`Service location: ${triggerLabel}`}
          startIcon={<Icon name="apartment" sx={{ fontSize: 20, color: COURT_GREEN }} />}
          endIcon={<Icon name="expand_more" sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: 'none',
            color: 'text.primary',
            fontSize: 14,
            fontWeight: 500,
            maxWidth: 260,
            minHeight: 44,
            '& .MuiButton-startIcon': { mr: 0.75 },
          }}
        >
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}
          >
            <Box
              component="span"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {triggerLabel}
            </Box>
            <ConnectionDot status={selectedLocation?.connectionStatus ?? null} />
          </Box>
        </Button>
      </Tooltip>
      <Menu anchorEl={anchor} open={open} onClose={handleClose}>
        {locations.map((loc) => (
          <MenuItem
            key={loc.id}
            selected={loc.id === selectedId}
            onClick={() => handleSelect(loc.id)}
            sx={{
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              '&.Mui-selected': { bgcolor: maps.greenTints.navActive },
            }}
          >
            <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0 }}>
              {loc.name ?? loc.id}
            </Box>
            <ConnectionDot status={loc.connectionStatus} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
